import apiClient, { getAccessToken } from './api'

const MAX_BACKOFF_MS = 30000

class DashboardSocketService {
  constructor() {
    this.eventSource = null
    this.listeners = new Map()
    this.seenEventIds = new Set()
    this.reconnectTimer = null
    this.backoffMs = 1000
    this.enabled = false
    this.onFallback = null
  }

  connect() {
    if (this.eventSource) return
    this.enabled = true
    this.open()
  }

  open() {
    if (!this.enabled) return
    const token = getAccessToken()
    if (!token || typeof window === 'undefined' || typeof window.EventSource !== 'function') {
      this.triggerFallback()
      return
    }

    const base = apiClient.defaults.baseURL || '/api'
    const url = new URL(`${base.replace(/\/$/, '')}/admin/dashboard/events`, window.location.origin)
    url.searchParams.set('access_token', token)
    const source = new window.EventSource(url.toString(), { withCredentials: true })
    this.eventSource = source

    source.onopen = () => {
      this.backoffMs = 1000
      this.emitLocal('socket:open', { connected: true })
    }

    source.onmessage = (event) => {
      try {
        const envelope = JSON.parse(event.data)
        const envelopeId = String(envelope?.id || '')
        if (envelopeId) {
          if (this.seenEventIds.has(envelopeId)) return
          this.seenEventIds.add(envelopeId)
          if (this.seenEventIds.size > 500) {
            const first = this.seenEventIds.values().next().value
            if (first) this.seenEventIds.delete(first)
          }
        }
        this.emitLocal('socket:event', envelope)
        this.emitLocal(envelope?.type, envelope)
      } catch {
        // Ignore malformed stream payloads.
      }
    }

    source.onerror = () => {
      this.emitLocal('socket:error', { connected: false })
      this.scheduleReconnect()
    }
  }

  scheduleReconnect() {
    if (!this.enabled) return
    this.disconnectCurrent()
    if (this.reconnectTimer) return
    this.triggerFallback()
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
      this.backoffMs = Math.min(MAX_BACKOFF_MS, this.backoffMs * 2)
    }, this.backoffMs)
  }

  disconnectCurrent() {
    if (!this.eventSource) return
    this.eventSource.close()
    this.eventSource = null
  }

  disconnect() {
    this.enabled = false
    this.disconnectCurrent()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  subscribe(type, handler) {
    if (!type || typeof handler !== 'function') return () => {}
    const rows = this.listeners.get(type) || []
    this.listeners.set(type, [...rows, handler])
    return () => {
      const current = this.listeners.get(type) || []
      this.listeners.set(type, current.filter((fn) => fn !== handler))
    }
  }

  setFallbackHandler(handler) {
    this.onFallback = typeof handler === 'function' ? handler : null
  }

  triggerFallback() {
    if (this.onFallback) this.onFallback()
  }

  emitLocal(type, payload) {
    const handlers = this.listeners.get(type) || []
    handlers.forEach((fn) => fn(payload))
  }
}

export default new DashboardSocketService()
